import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  createFileRoute,
  useRouter,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import { useState } from "react"
import { Student } from "@/api/types/students"
import {
  DataTable,
  createActionsColumn,
  createCustomColumn,
  selectColumn,
  createSortableColumn,
  formatBool,
  getFullName,
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  TABLE_PAGE_SIZE,
  studentTypeMap,
  Badge,
  getStudentStatusLabel,
  studentStatusToBadgeVariant,
  MultiLevelDropdownMenu,
  useExportMutation,
  Button,
  formatDateToDutch,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import DocumentModal from "@/components/modals/DocumentModal"
import { studentQueryKeys } from "@/api/query-keys/students"
import { useScheduleIntakeStore } from "@/stores/useScheduleIntakeStore"
import {
  STUDENT_STORAGE_KEY,
  useStudentFilters,
} from "@/hooks/filters/useStudentFilters"
import { z } from "zod"
import { ArchiveBoxIcon } from "@heroicons/react/24/outline"
import ArchiveStudentModal from "@/components/modals/students/ArchiveStudentModal"
import UnarchiveStudentModal from "@/components/modals/students/UnarchiveStudentModal"
import StudentTemporaryAbsenceModal from "@/components/modals/students/StudentTemporaryAbsenceModal"
import ResendStudentInviteModal from "@/components/modals/students/ResendStudentInviteModal"
import { hasRole } from "@/utils/can"

export const Route = createFileRoute("/beheer/cursisten/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "students"),
  validateSearch: createTableSearchSchema({
    defaultSortField: "id",
    defaultSortDirection: "asc",
  }).extend({
    archived: z.boolean().optional().catch(false),
  }),
})

const DEFAULT_COLUMN_VISIBILITY = {
  postalCode: false,
  organization: false,
  type: false,
  moduleCode: false,
}

const ALL_COLUMN_IDS = [
  "id",
  "firstName",
  "email",
  "phoneNumberCall",
  "city",
  "appInstalled",
  "status",
  "postalCode",
  "organization",
  "studentType",
]

function RouteComponent() {
  const [activeRow, setActiveRow] = useState<Student | null>(null)
  const [temporaryAbsenceOpen, setTemporaryAbsenceOpen] = useState(false)
  const [uploadDocumentOpen, setUploadDocumentOpen] = useState(false)
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [isUnarchiveOpen, setIsUnarchiveOpen] = useState(false)
  const [isResendInviteOpen, setIsResendInviteOpen] = useState(false)

  const router = useRouter()
  const { clearIntake } = useScheduleIntakeStore()
  const searchParams = useSearch({ from: "/beheer/cursisten/" })
  const navigate = useNavigate({ from: "/beheer/cursisten/" })
  const { archived } = searchParams

  const { filterItems, activeFilters, queryFn } = useStudentFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
    archived,
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
  } = usePaginatedTableWithUrlState({
    queryKey: [...studentQueryKeys.all, activeFilters, { archived }],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: STUDENT_STORAGE_KEY,
    defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const handleNavigateToStudentDetail = (studentId: Student["id"]) => {
    router.navigate({
      to: "/beheer/cursisten/$studentId/gegevens",
      params: { studentId: studentId.toString() },
      search: { from: router.state.location.href },
    })
  }

  const columns: ColumnDef<Student>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createCustomColumn({
      id: "firstName",
      name: "Naam",
      cell: ({ row }: { row: { original: Student } }) =>
        getFullName({
          firstName: row.original.firstName,
          lastName: row.original.lastName,
        }),
      accessorKey: "firstName",
    }),
    createSortableColumn({ accessorKey: "email", name: "E-mailadres" }),
    createSortableColumn({
      accessorKey: "phoneNumberCall",
      name: "Telefoonnummer",
    }),
    createCustomColumn({
      id: "birthDate",
      accessorKey: "birthDate",
      name: "Geboortedatum",
      cell: ({ row }: { row: { original: Student } }) =>
        formatDateToDutch(row.original.birthDate, { format: "date" }),
    }),
    createSortableColumn({ accessorKey: "city", name: "Woonplaats" }),
    createCustomColumn({
      id: "appInstalled",
      name: "App",
      cell: ({ row }: { row: { original: Student } }) => (
        <span>{formatBool(row.original.appInstalled)}</span>
      ),
      accessorKey: "appInstalled",
    }),
    createCustomColumn({
      id: "status",
      name: "Status",
      cell: ({ row }: { row: { original: Student } }) => {
        const status = row.original.currentStudentProjectOrganization?.status

        return (
          <Badge variant={studentStatusToBadgeVariant(status)}>
            {getStudentStatusLabel(status)}
          </Badge>
        )
      },
      accessorKey: "status",
      enableSorting: false,
    }),
    createSortableColumn({ accessorKey: "postalCode", name: "Postcode" }),
    createCustomColumn({
      id: "organization",
      name: "Organisatie",
      cell: ({ row }: { row: { original: Student } }) =>
        row.original.currentStudentProjectOrganization?.organization.name ||
        "-",
      accessorKey: "projectOrganizations",
      enableSorting: false,
    }),
    createCustomColumn({
      id: "studentType",
      name: "Type",
      cell: ({ row }: { row: { original: Student } }) =>
        row.original.currentStudentProjectOrganization?.studentType
          ? studentTypeMap[
              row.original.currentStudentProjectOrganization?.studentType
            ] || row.original.currentStudentProjectOrganization?.studentType
          : "Nog niet bekend",
      accessorKey: "studentType",
    }),
    createActionsColumn([
      {
        label: "Tijdelijk afwezig melden",
        onClick: (e, student) => {
          e.stopPropagation()
          setActiveRow(student)
          setTemporaryAbsenceOpen(true)
        },
        hidden: (student) => {
          const status = student.currentStudentProjectOrganization?.status

          return (
            status === "dropped_out" ||
            status === "finished" ||
            status === "interrupted"
          )
        },
      },
      {
        label: "Document uploaden",
        onClick: (e, student) => {
          e.stopPropagation()
          setActiveRow(student)
          setUploadDocumentOpen(true)
        },
      },
      {
        label: "Intake plannen",
        onClick: (e, student) => {
          e.stopPropagation()
          clearIntake()
          router.navigate({
            to: "/beheer/intakes/intake-plannen",
            search: { studentId: student.id },
          })
        },
        hidden: ({ mustPlanIntake }) => mustPlanIntake === false,
      },
      {
        label: "Uitnodiging opnieuw versturen",
        onClick: (e, student) => {
          e.stopPropagation()
          setActiveRow(student)
          setIsResendInviteOpen(true)
        },
        hidden: (student) =>
          student.currentStudentProjectOrganization?.status !==
            "never_logged_in" ||
          !(hasRole("admin") || hasRole("executiveEmployee")),
      },
      {
        label: "Cursist archiveren",
        onClick: (e, student) => {
          e.stopPropagation()
          setActiveRow(student)
          setIsArchiveOpen(true)
        },
        hidden: archived,
      },
      {
        label: "Cursist herstellen",
        onClick: (e, student) => {
          e.stopPropagation()
          setActiveRow(student)
          setIsUnarchiveOpen(true)
        },
        hidden: !archived,
      },
    ]),
  ]

  const handleToggleArchived = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        archived: !archived,
        page: 1,
      }),
    })
  }

  const { mutate: exportStudents } = useExportMutation({
    exportApiPrefix: "students",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title={archived ? "Cursisten archief" : "Cursisten"}
        data={data?.data.students || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        onRowClick={handleNavigateToStudentDetail}
        columns={columns}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        resetColumnVisibility={resetColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        headerLeftActions={
          <>
            <MultiLevelDropdownMenu items={filterItems} />
            <Button
              onClick={handleToggleArchived}
              variant={archived ? "primary" : "secondary"}
              icon={<ArchiveBoxIcon className="size-5" />}
            >
              Archief
            </Button>
          </>
        }
        headerRightActions={
          <TableHeaderActions
            exportAction={{
              onClick: exportStudents,
            }}
            createAction={{
              label: "Nieuwe cursist",
              to: "/beheer/cursisten/aanmaken",
              entity: "students",
              permissions: "create",
            }}
          />
        }
      />

      {temporaryAbsenceOpen && activeRow && (
        <StudentTemporaryAbsenceModal
          isOpen={temporaryAbsenceOpen}
          onClose={() => setTemporaryAbsenceOpen(false)}
          studentId={activeRow.id}
        />
      )}

      {uploadDocumentOpen && activeRow && (
        <DocumentModal
          open={uploadDocumentOpen}
          setOpen={setUploadDocumentOpen}
          id={activeRow.id}
          title={`Document uploaden voor ${getFullName({ firstName: activeRow.firstName, lastName: activeRow.lastName })}`}
          entity="STUDENT"
          queryKey={studentQueryKeys.documents(activeRow.id)}
          showCategory={true}
        />
      )}

      {isArchiveOpen && activeRow && (
        <ArchiveStudentModal
          isOpen={isArchiveOpen}
          setIsOpen={setIsArchiveOpen}
          student={activeRow}
        />
      )}

      {isUnarchiveOpen && activeRow && (
        <UnarchiveStudentModal
          isOpen={isUnarchiveOpen}
          setIsOpen={setIsUnarchiveOpen}
          student={activeRow}
        />
      )}

      {isResendInviteOpen && activeRow && (
        <ResendStudentInviteModal
          isOpen={isResendInviteOpen}
          setIsOpen={setIsResendInviteOpen}
          student={activeRow}
        />
      )}
    </ScreenWrapper>
  )
}
